import React, { useState } from 'react';
import { Search, Filter, MoreHorizontal, ChevronLeft, ChevronRight } from 'lucide-react';
interface User {
  id: string;
  name: string;
  email: string;
  status: 'active' | 'inactive' | 'pending';
  registrationDate: string;
  lastActive: string;
  accountType: string;
}
interface UserTableProps {
  users: User[];
  onViewProfile: (userId: string) => void;
}
const UserTable: React.FC<UserTableProps> = ({
  users,
  onViewProfile
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  // Filter users based on search term
  const filteredUsers = users.filter(user => user.name.toLowerCase().includes(searchTerm.toLowerCase()) || user.email.toLowerCase().includes(searchTerm.toLowerCase()) || user.id.toLowerCase().includes(searchTerm.toLowerCase()));
  // Get current users
  const indexOfLastUser = currentPage * itemsPerPage;
  const indexOfFirstUser = indexOfLastUser - itemsPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  // Change page
  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200" data-id="element-117">
            Active
          </span>;
      case 'inactive':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200" data-id="element-118">
            Inactive
          </span>;
      case 'pending':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200" data-id="element-119">
            Pending
          </span>;
      default:
        return null;
    }
  };
  return <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden" data-id="element-120">
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-121">
        <div className="relative w-full sm:w-64" data-id="element-122">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-123">
            <Search size={18} className="text-gray-400" data-id="element-124" />
          </div>
          <input type="text" placeholder="Search users..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md w-full bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400" data-id="element-125" />
        </div>
        <div className="flex space-x-2" data-id="element-126">
          <button className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600" data-id="element-127">
            <Filter size={16} className="mr-2" data-id="element-128" />
            Filter
          </button>
          <button className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-129">
            Export
          </button>
        </div>
      </div>
      <div className="overflow-x-auto" data-id="element-130">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-131">
          <thead className="bg-gray-50 dark:bg-gray-700" data-id="element-132">
            <tr data-id="element-133">
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-134">
                User
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-135">
                Status
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-136">
                Registration Date
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-137">
                Last Active
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-138">
                Account Type
              </th>
              <th scope="col" className="relative px-6 py-3" data-id="element-139">
                <span className="sr-only" data-id="element-140">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" data-id="element-141">
            {currentUsers.map(user => <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-142">
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-143">
                  <div className="flex items-center" data-id="element-144">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-gray-500 dark:text-gray-300" data-id="element-145">
                      {user.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="ml-4" data-id="element-146">
                      <div className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-147">
                        {user.name}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-148">
                        {user.email}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-149">
                  {getStatusBadge(user.status)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-150">
                  {user.registrationDate}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-151">
                  {user.lastActive}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-152">
                  {user.accountType}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-153">
                  <button onClick={() => onViewProfile(user.id)} className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 mr-4" data-id="element-154">
                    View
                  </button>
                  <button className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300" data-id="element-155">
                    <MoreHorizontal size={16} data-id="element-156" />
                  </button>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600 sm:px-6" data-id="element-157">
        <div className="flex items-center justify-between" data-id="element-158">
          <div className="text-sm text-gray-700 dark:text-gray-300" data-id="element-159">
            Showing <span className="font-medium" data-id="element-160">{indexOfFirstUser + 1}</span>{' '}
            to{' '}
            <span className="font-medium" data-id="element-161">
              {indexOfLastUser > filteredUsers.length ? filteredUsers.length : indexOfLastUser}
            </span>{' '}
            of <span className="font-medium" data-id="element-162">{filteredUsers.length}</span>{' '}
            results
          </div>
          <div className="flex-1 flex justify-end" data-id="element-163">
            <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1} className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium ${currentPage === 1 ? 'text-gray-300 dark:text-gray-500' : 'text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'}`} data-id="element-164">
              <ChevronLeft size={16} data-id="element-165" />
            </button>
            <button onClick={() => paginate(currentPage + 1)} disabled={indexOfLastUser >= filteredUsers.length} className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium ${indexOfLastUser >= filteredUsers.length ? 'text-gray-300 dark:text-gray-500' : 'text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'}`} data-id="element-166">
              <ChevronRight size={16} data-id="element-167" />
            </button>
          </div>
        </div>
      </div>
    </div>;
};
export default UserTable;